import { createInstance, dateReviver } from '@/api';
import {
    ProductProcessDefinitionClient,
    OrderDirection,
    ProductProcessDefinitionSearchResultDto,
    OrganisationSearchResultDto
} from '@/api/backoffice/clients';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { i18n } from '@/i18n';

export interface ProductProcessDefinitionFilter {
    id?: number | null;
    label?: string;
    description?: string;
    order?: string;
    orderDirection?: OrderDirection;
    pageNumber?: number;
    pageSize?: number;
    organisation?: OrganisationSearchResultDto;
    statusType?: string;
}

export interface ProductProcessDefinitionStoreState {
    filter: ProductProcessDefinitionFilter;
    loading: boolean;
    items?: ProductProcessDefinitionSearchResultDto[];
    totalCount?: number;
}

// Must stay in sync with the column marked as default sorted column in ProductProcessDefinitionSearch.vue.
export const DEFAULT_ORDER = 'label';

export const client = new ProductProcessDefinitionClient('', createInstance(), dateReviver);

export const useProductProcessDefinitionListStore = defineStore('configuration-product-process-definition-list-store', {
    state: (): ProductProcessDefinitionStoreState => ({
        filter: {
            order: DEFAULT_ORDER,
            orderDirection: OrderDirection.Ascending,
            pageNumber: 1,
            pageSize: 25,
            organisation: null
        },
        totalCount: 0,
        loading: false,
        items: []
    }),
    getters: {
        metaData(): {
            totalRows: number;
            resultsPerPage: number;
            currentPage: number | undefined;
            serverside: boolean;
        } {
            return {
                totalRows: this.totalCount,
                resultsPerPage: this.filter.pageSize,
                currentPage: this.filter.pageNumber,
                serverside: true
            };
        },
        getDefinition(): (id: number) => ProductProcessDefinitionSearchResultDto | undefined {
            return (id: number) => this.items?.find((item) => item.id === id);
        },
        search(): (query: string) => ProductProcessDefinitionSearchResultDto[] {
            return (query) => {
                if (!query) {
                    return this.items;
                } else {
                    return this.items?.filter((x) => x.label?.toLowerCase().includes(query.toLowerCase()));
                }
            };
        }
    },
    actions: {
        init(filter: ProductProcessDefinitionFilter) {
            this.filter = { ...this.filter, ...filter };
            this.items = [];
        },
        clearFilter() {
            this.filter = {
                order: DEFAULT_ORDER,
                orderDirection: OrderDirection.Ascending,
                pageNumber: 1,
                pageSize: 25,
                organisation: null
            };

            useAlertStore().clearAlert();

            this.items = [];
        },
        resetPager() {
            this.filter.pageNumber = 1;
        },
        updateItem(item: ProductProcessDefinitionSearchResultDto) {
            const index = this.items?.findIndex((i) => i.id === item.id);
            if (index !== undefined && index !== -1) {
                this.items?.splice(index, 1, item);
            }
        },
        async getDefinitions() {
            this.loading = true;

            useAlertStore().clearAlert();

            const order = mapToQueryableOrder(this.filter.order);
            try {
                const response = await client.search(
                    this.filter.id,
                    this.filter.label,
                    this.filter.description,
                    null,
                    this.filter.organisation?.ovoNumber,
                    null,
                    this.filter.statusType,
                    order,
                    this.filter.orderDirection,
                    this.filter.pageNumber,
                    this.filter.pageSize
                );

                this.items = response.items;
                this.filter.pageNumber = response.page;
                this.filter.pageSize = response.pageSize;
                this.totalCount = response.totalCount;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('productprocessdefinition.list.error.title'),
                    error,
                    false
                );
            } finally {
                this.loading = false;
            }
        },
        updatePage(pageNumber: number) {
            this.filter.pageNumber = pageNumber;
        },
        updateSorting(column: string, order: string) {
            this.filter.order = column;
            this.filter.orderDirection = order === 'Asc' ? OrderDirection.Ascending : OrderDirection.Descending;
        },
        async getAll({ silent = false } = {}) {
            if (!silent) {
                this.loading = false;
            }

            useAlertStore().clearAlert();
            try {
                const response = await client.getAll();
                this.items = response;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('productprocessdefinition.list.error.title'),
                    error,
                    false
                );
            } finally {
                this.loading = false;
            }

            return this.items;
        }
    }
});

function mapToQueryableOrder(order: string) {
    switch (order) {
        case 'statusId':
            return 'status';
        case 'ovoNumber':
            return 'organisation.ovoNumber';
        case 'organisation':
            return 'organisation.name';
        default:
            return order;
    }
}
